import { Text } from "@mantine/core";
import { IconMicrophone } from "@tabler/icons-react";
import { useEffect, useRef } from "react";
import type { AssistantVoiceText } from "@/locales/assistant-voice";
import { isEditableTarget, isSpaceKey } from "./voice-noc.logic";

/** Tinggi minimum sasaran sentuh (px); layar wall disentuh dari jauh. */
const PTT_MIN_HEIGHT_PX = 64;

interface Props {
	t: AssistantVoiceText;
	held: boolean;
	enabled: boolean;
	onPress(): void;
	onRelease(): void;
}

/**
 * Tombol tekan-untuk-bicara. Listener asli (bukan props React) supaya lepas
 * pointer, blur jendela, tab tersembunyi, dan unmount selalu melepas tombol
 * (mic dibisukan lagi) dan tidak pernah macet terbuka.
 */
export function VoicePttButton({
	t,
	held,
	enabled,
	onPress,
	onRelease,
}: Props) {
	const ref = useRef<HTMLButtonElement>(null);
	const press = useRef(onPress);
	const release = useRef(onRelease);
	press.current = onPress;
	release.current = onRelease;

	useEffect(() => {
		const button = ref.current;
		if (!button || !enabled) return;
		const down = (e: Event) => {
			e.preventDefault();
			press.current();
		};
		const up = () => release.current();
		const keyDown = (e: KeyboardEvent) => {
			if (!isSpaceKey(e) || isEditableTarget(e.target)) return;
			e.preventDefault();
			if (!e.repeat) press.current();
		};
		const keyUp = (e: KeyboardEvent) => {
			if (isSpaceKey(e)) up();
		};
		const hidden = () => {
			if (document.visibilityState === "hidden") up();
		};
		const noMenu = (e: Event) => e.preventDefault();
		button.addEventListener("pointerdown", down);
		button.addEventListener("pointerup", up);
		button.addEventListener("pointercancel", up);
		button.addEventListener("pointerleave", up);
		button.addEventListener("contextmenu", noMenu);
		window.addEventListener("keydown", keyDown);
		window.addEventListener("keyup", keyUp);
		window.addEventListener("blur", up);
		document.addEventListener("visibilitychange", hidden);
		return () => {
			button.removeEventListener("pointerdown", down);
			button.removeEventListener("pointerup", up);
			button.removeEventListener("pointercancel", up);
			button.removeEventListener("pointerleave", up);
			button.removeEventListener("contextmenu", noMenu);
			window.removeEventListener("keydown", keyDown);
			window.removeEventListener("keyup", keyUp);
			window.removeEventListener("blur", up);
			document.removeEventListener("visibilitychange", hidden);
			up();
		};
	}, [enabled]);

	return (
		<>
			<button
				ref={ref}
				type="button"
				disabled={!enabled}
				aria-pressed={held}
				data-ptt-button
				style={{
					minHeight: PTT_MIN_HEIGHT_PX,
					width: "100%",
					borderRadius: 12,
					border: "2px solid currentColor",
					cursor: enabled ? "pointer" : "not-allowed",
					touchAction: "none",
					userSelect: "none",
					WebkitUserSelect: "none",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 8,
					fontSize: 18,
					fontWeight: 700,
					background: held ? "var(--mantine-color-green-9)" : "transparent",
					color: held ? "#fff" : "inherit",
				}}
			>
				<IconMicrophone size={26} aria-hidden />
				{held ? t.noc.pttHolding : t.noc.pttHold}
			</button>
			<Text size="xs" c="dimmed" ta="center">
				{t.noc.pttSpaceHint}
			</Text>
		</>
	);
}
